// Export history page.
//
// Replaces the old "Saved reports" placeholder with a log of everything the
// customer has actually pulled or sent: composed documents (download / email)
// and standalone table downloads. Each row keeps enough recipe to re-download
// on demand, so the history doubles as a quick re-run surface.
//
// Postgres mapping: reads `user_exports` (see exportData.js logExport) ordered
// by created_at desc; "Download again" re-resolves current data rather than
// storing the rendered blob.
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, Tooltip, Snackbar } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MainCard from '../../../components/cards/MainCard';
import useFeature from '../useFeature';
import { assembleSections, LARGE_TABLES, downloadBlob, exportPeriodLabel, largeTableRequest, largeTableLiveRows } from '../exportData';
import { requestsFor, assembleLive } from '../server/exportCore';
import { resolveDateRange, tableDateRange } from '../hooks/useDashboardData';
import { dwmApi } from '../../../store/services/dwm-service';
import { getExcludeDisabled } from '../disabledFilter';
import { dashboardApi } from '../../../store/services/dashboard.service';
import { generateDocument, generateTable } from '../exportGenerators';
import { useGetExportHistoryQuery, useClearExportHistoryMutation } from '../../../store/services/reportLayout.service';

function fmtAt(iso) {
    try {
        return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}

const VIA_LABEL = { download: 'Downloaded', email: 'Emailed', scheduled: 'Scheduled' };

// Entries from before BD-1124 kept a period but not its dates. A preset can
// still be re-resolved against today; a custom range is lost.
export const lostCustomRange = (e) => e.period === 'custom' && !(e.startDate && e.endDate);

// The window an entry covered: its saved dates, else its period resolved now.
export const savedRange = (e, resolve) => (e.startDate && e.endDate ? { start: e.startDate, end: e.endDate } : resolve(e));

export const historyPeriodLabel = (e) => {
    if (e.startDate && e.endDate) return exportPeriodLabel('custom', e.startDate, e.endDate);
    return e.period ? exportPeriodLabel(e.period) : null;
};

export default function ExportHistoryPage() {
    const benchmarkOn = useFeature('industryBenchmarking');
    const { tenancyId, tenancyName } = useSelector((state) => state.tenancy);
    const brand = (tenancyName || '').trim() || 'Boxphish';
    // Server-side per-tenancy history - shared across admins and devices.
    const historyQ = useGetExportHistoryQuery(tenancyId, { skip: tenancyId == null });
    const [clearHistory] = useClearExportHistoryMutation();
    const history = historyQ.data?.entries ?? [];
    const [toast, setToast] = useState('');
    const dwmOn = useFeature('darkWebMonitoring');
    const [fetchDashboard] = dashboardApi.endpoints.getDashboardData.useLazyQuery();
    const [fetchDwmStats] = dwmApi.endpoints.getDwmBreachStats.useLazyQuery();
    const [fetchDwmTrend] = dwmApi.endpoints.getDwmBreachTrend.useLazyQuery();
    const [fetchDwmUsers] = dwmApi.endpoints.getDwmBreachedUsers.useLazyQuery();

    // Entries from before BD-1124 carry no period; those tables were the browser's
    // 13 months, so all time is the closest honest re-pull.
    const liveTableRows = async (e) => {
        const tenancy = e.scopeQuery?.tenancy;
        const scope = Array.isArray(tenancy) && tenancy.length ? { tenancy } : undefined;
        const request = largeTableRequest(e.tableId, scope);
        const data = await fetchDashboard({
            tenancyId: Number(tenancyId),
            ...request,
            ...(getExcludeDisabled() ? { scope: { ...request.scope, excludeDisabled: true } } : {}),
            filters: { dateRange: savedRange(e, () => tableDateRange({ period: e.period || 'all' })) },
        }, true).unwrap();
        return largeTableLiveRows(e.tableId, data);
    };

    // Replays the builder's server calls for these sections over the saved
    // window and filter, so the document matches what was originally pulled.
    const dwmCalls = {
        stats: (r) => fetchDwmStats({ tenancyId, startDate: r.start, endDate: r.end }, true),
        trend: (r) => fetchDwmTrend({ tenancyId, startDate: r.start, endDate: r.end, granularity: 'auto' }, true),
        byEmail: (r) => fetchDwmUsers({ tenancyId, page: 1, limit: 100, startDate: r.start, endDate: r.end }, true),
    };
    const liveForDocument = async (e) => {
        const dateRange = savedRange(e, resolveDateRange);
        const raw = {};
        await Promise.all(requestsFor(e.sectionIds, { dwmOn, benchmarkOn }).map(async (spec) => {
            const call = spec.dwm
                ? dwmCalls[spec.dwm](dateRange)
                : fetchDashboard({ tenancyId: Number(tenancyId), ...spec.body, scope: e.scopeQuery, filters: { dateRange } }, true);
            raw[spec.key] = await call.unwrap();
        }));
        return assembleLive(raw);
    };

    const tableFile = async (e) => {
        const t = LARGE_TABLES.find((x) => x.id === e.tableId);
        if (!t) return { message: 'That table is no longer available' };
        // Dark web mentions come from the dwm-api, not the dashboard endpoint.
        if (!largeTableRequest(e.tableId)) return { message: `${t.label} can't be re-downloaded from here. Download it again from Data tables.` };
        return { file: generateTable(e.format, t.label, t.columns, t.getRows(await liveTableRows(e)), { brand }) };
    };

    const documentFile = async (e) => {
        const live = await liveForDocument(e);
        const sections = assembleSections(e.sectionIds || [], e.drillIds || [], { benchmarkOn, period: e.period }, live);
        if (sections.length === 0) return { message: 'Those sections are no longer available' };
        return { file: await generateDocument(e.format, e.title, sections, { brand, periodLabel: historyPeriodLabel(e), scopeLabel: e.scope }) };
    };

    const redownload = async (e) => {
        if (lostCustomRange(e)) {
            setToast('This export used a custom date range that was not saved. Run it again from the Export builder or Data tables.');
            return;
        }
        try {
            const { file, message } = e.kind === 'table' ? await tableFile(e) : await documentFile(e);
            if (!file) { setToast(message); return; }
            downloadBlob(file.content, file.mime, file.filename);
            setToast(`Re-downloaded ${file.filename}`);
        } catch {
            setToast('Could not re-download this export');
        }
    };

    const clearAll = async () => {
        try {
            await clearHistory(tenancyId).unwrap();
            setToast('History cleared');
        } catch {
            setToast('Could not clear the history - please try again');
        }
    };

    return (
        <Box sx={{ p: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>Export history</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                    Every export you have pulled or sent. Re-download any of them with the latest data.
                </Typography>
            </Box>

            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>Pulled exports</Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{history.length} record{history.length === 1 ? '' : 's'}</Typography>
                    </Box>
                    {history.length > 0 && (
                        <Button
                            size="small" variant="outlined"
                            startIcon={<DeleteSweepOutlinedIcon sx={{ fontSize: 15 }} />}
                            onClick={clearAll}
                            sx={{ textTransform: 'none', fontSize: 11, borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'error.main', color: 'error.main' } }}
                        >
                            Clear history
                        </Button>
                    )}
                </Box>

                {history.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{historyQ.isLoading ? 'Loading history\u2026' : 'No exports yet.'}</Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>Anything you download or email from the export builder or data tables shows up here.</Typography>
                    </Box>
                ) : (
                    <Box sx={{ p: 1.5 }}>
                        {history.map((e) => {
                            const Icon = e.kind === 'table' ? TableChartOutlinedIcon : DescriptionOutlinedIcon;
                            const detail = e.kind === 'table'
                                ? `${(e.rows ?? 0).toLocaleString()} rows`
                                : `${e.sectionCount ?? (e.sectionIds ? e.sectionIds.length : 0)} sections`;
                            return (
                                <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 1.5, py: 1.25, borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}>
                                    <Icon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
                                    <Box sx={{ flex: 1, minWidth: 180 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.title}</Typography>
                                            <Typography sx={{ fontSize: 9.5, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.75 }}>{e.format}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
                                            {fmtAt(e.at)} · {detail}
                                            {/* Who pulled it. Stamped server-side from the JWT, so it's an
                                                audit trail rather than something the client asserted.
                                                Older entries predate the field and simply omit it. */}
                                            {e.byEmail ? ` · by ${e.byEmail}` : ''}
                                            {e.via === 'email' && e.recipients?.length ? ` · to ${e.recipients.join(', ')}` : ''}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 10.5, color: 'text.disabled', flexShrink: 0 }}>
                                        {e.via === 'email' && <MailOutlineIcon sx={{ fontSize: 13 }} />}
                                        {VIA_LABEL[e.via] || e.via}
                                    </Box>
                                    <Tooltip title="Download again with the latest data">
                                        <Button
                                            size="small" variant="outlined"
                                            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 13 }} />}
                                            onClick={() => redownload(e)}
                                            sx={{ minWidth: 0, fontSize: 10.5, fontWeight: 600, px: 1, py: '2px', borderRadius: '4px', textTransform: 'none', borderColor: 'divider', color: 'text.secondary', flexShrink: 0, '& .MuiButton-startIcon': { mr: 0.5 }, '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' } }}
                                        >
                                            Again
                                        </Button>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </MainCard>

            <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}
